import { Backdrop, Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme/theme";

// Fullscreen overlay shown while the basegrid is being generated.
// Theme: several external data sources (satellite, maps, population, elevation,
// database, GIS) around the edges continuously stream little data packets
// towards the center, where a small network pulses as it is assembled from the
// incoming information. Below it rotating status messages.

const MESSAGES = [
  "Fetching elevation data...",
  "Querying map data...",
  "Loading population data...",
  "Connecting the network...",
  "Assembling nodes and pipes...",
];

const CENTER = { x: 225, y: 145 };

// Data sources around the perimeter, each with its own emoji, label and packet
// color so it reads as a distinct stream feeding into the network.
const SOURCES = [
  { x: 55, y: 50, icon: "🛰️", label: "Satellite", color: "hsl(210, 85%, 55%)" },
  { x: 395, y: 50, icon: "🗺️", label: "Map", color: "hsl(160, 70%, 45%)" },
  { x: 55, y: 240, icon: "🗄️", label: "Database", color: "hsl(280, 60%, 62%)" },
  { x: 395, y: 240, icon: "⛰️", label: "Elevation", color: "hsl(30, 85%, 55%)" },
];

// Small network assembled in the center. Positions are absolute in the viewBox.
const NET_NODES = [
  { x: 225, y: 145 }, // hub
  { x: 198, y: 120 },
  { x: 252, y: 124 },
  { x: 202, y: 172 },
  { x: 250, y: 168 },
  { x: 225, y: 108 },
  { x: 262, y: 150 },
];
const NET_EDGES = [
  [0, 1],
  [0, 2],
  [0, 3],
  [0, 4],
  [1, 5],
  [2, 6],
  [4, 6],
];

// packets emitted per source (staggered) so each stream looks continuous
const PACKETS_PER_SOURCE = 3;
const PACKET_DURATION = 1.9; // s a packet takes to reach the center

export const BasegridOverlay = ({ active }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [messageIndex, setMessageIndex] = useState(0);
  useEffect(() => {
    if (!active) return;
    setMessageIndex(0);
    const interval = setInterval(
      () => setMessageIndex((i) => (i + 1) % MESSAGES.length),
      2400
    );
    return () => clearInterval(interval);
  }, [active]);

  const pipeColor = "hsl(210, 85%, 50%)";
  const nodeColor = "hsl(210, 85%, 40%)";
  const hubColor = "hsl(210, 90%, 55%)";
  const labelColor = theme.palette.mode === "dark" ? "#bbb" : "#666";

  return (
    <Backdrop
      open={Boolean(active)}
      sx={{
        zIndex: 99999999,
        bgcolor: colors.contrast[200] + "80",
        backdropFilter: "blur(10px)",
      }}
    >
      {Boolean(active) && (
        <Box
          className="flex flex-col items-center gap-5"
          sx={{
            "& .bg-net-edge": {
              stroke: pipeColor,
              strokeWidth: 2.5,
              strokeLinecap: "round",
              opacity: 0.85,
            },
            "& .bg-net-node": {
              fill: nodeColor,
              transformBox: "fill-box",
              transformOrigin: "center",
            },
            "& .bg-hub": {
              fill: hubColor,
              transformBox: "fill-box",
              transformOrigin: "center",
              animation: "bgHubPulse 1.6s ease-in-out infinite",
            },
            "& .bg-hub-ring": {
              fill: "none",
              stroke: hubColor,
              strokeWidth: 2,
              transformBox: "fill-box",
              transformOrigin: "center",
              animation: "bgRing 1.8s ease-out infinite",
            },
            "& .bg-source": {
              transformBox: "fill-box",
              transformOrigin: "center",
              animation: "bgSourceBob 2.4s ease-in-out infinite",
            },
            "& .bg-source-label": {
              fill: labelColor,
              fontSize: "8px",
            },
            "@keyframes bgFly": {
              "0%": {
                transform: "translate(var(--fx), var(--fy)) scale(0.5)",
                opacity: 0,
              },
              "12%": { opacity: 1 },
              "82%": { opacity: 1 },
              "100%": {
                transform: "translate(var(--tx), var(--ty)) scale(0.35)",
                opacity: 0,
              },
            },
            "@keyframes bgHubPulse": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.25)" },
            },
            "@keyframes bgRing": {
              "0%": { transform: "scale(0.5)", opacity: 0.9 },
              "100%": { transform: "scale(2.4)", opacity: 0 },
            },
            "@keyframes bgNode": {
              "0%, 100%": { transform: "scale(1)", opacity: 0.85 },
              "50%": { transform: "scale(1.3)", opacity: 1 },
            },
            "@keyframes bgSourceBob": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-3px)" },
            },
            "@keyframes bgFadeIn": {
              from: { opacity: 0, transform: "translateY(6px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <svg
            width="480"
            height="310"
            viewBox="0 0 450 290"
            style={{ overflow: "visible", maxWidth: "90vw" }}
          >
            {/* faint guide lines from each source to the center */}
            {SOURCES.map((s, i) => (
              <line
                key={`guide-${i}`}
                x1={s.x}
                y1={s.y}
                x2={CENTER.x}
                y2={CENTER.y}
                stroke={s.color}
                strokeWidth={1}
                strokeDasharray="2 6"
                opacity={0.25}
              />
            ))}

            {/* central network */}
            {NET_EDGES.map(([a, b], i) => (
              <line
                key={`ne-${i}`}
                className="bg-net-edge"
                x1={NET_NODES[a].x}
                y1={NET_NODES[a].y}
                x2={NET_NODES[b].x}
                y2={NET_NODES[b].y}
              />
            ))}
            {NET_NODES.slice(1).map((n, i) => (
              <circle
                key={`nn-${i}`}
                className="bg-net-node"
                cx={n.x}
                cy={n.y}
                r={4}
                style={{
                  animation: `bgNode 1.8s ease-in-out ${i * 0.22}s infinite`,
                }}
              />
            ))}
            {/* hub with expanding ring */}
            <circle
              className="bg-hub-ring"
              cx={CENTER.x}
              cy={CENTER.y}
              r={9}
            />
            <circle className="bg-hub" cx={CENTER.x} cy={CENTER.y} r={6} />

            {/* data packets flying from each source into the center */}
            {SOURCES.map((s, si) =>
              Array.from({ length: PACKETS_PER_SOURCE }).map((_, pi) => (
                <g
                  key={`p-${si}-${pi}`}
                  style={{
                    "--fx": `${s.x}px`,
                    "--fy": `${s.y + 12}px`,
                    "--tx": `${CENTER.x}px`,
                    "--ty": `${CENTER.y}px`,
                    animation: `bgFly ${PACKET_DURATION}s linear ${
                      (pi * PACKET_DURATION) / PACKETS_PER_SOURCE + si * 0.15
                    }s infinite`,
                  }}
                >
                  <rect
                    x={-3}
                    y={-3}
                    width={6}
                    height={6}
                    rx={1.5}
                    fill={s.color}
                  />
                </g>
              ))
            )}

            {/* source icons + labels */}
            {SOURCES.map((s, i) => (
              <g key={`s-${i}`} className="bg-source">
                <text
                  x={s.x}
                  y={s.y + 5}
                  textAnchor="middle"
                  fontSize="22"
                  style={{ userSelect: "none" }}
                >
                  {s.icon}
                </text>
                <text
                  className="bg-source-label"
                  x={s.x}
                  y={s.y + 20}
                  textAnchor="middle"
                >
                  {t(s.label)}
                </text>
              </g>
            ))}
          </svg>

          <Typography variant="h3">{t("Generating basegrid")}</Typography>
          <Typography
            key={messageIndex}
            variant="h5"
            sx={{ opacity: 0.8, animation: "bgFadeIn 0.5s ease-out" }}
          >
            {t(MESSAGES[messageIndex])}
          </Typography>
        </Box>
      )}
    </Backdrop>
  );
};

export default BasegridOverlay;
